import { SendIcon } from "../../../../components/icons/SendIcon";
import { useTheme } from "../../../../context/ThemeProvider";
import { updateSendMessageData } from "../../../../store/Slices/SendMessageSlice";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import EmojiPickerCompo from "./EmojiPickerCompo";
import useApiPost from "../../../../hooks/PostData";
import { Message } from "../../../../types/MessageListType";
import SelectFile from "./SelectFile";
import AttachmentDraftStrip from "./AttachmentDraftStrip";
import LinkDraftPreview from "./LinkDraftPreview";
import { useFile } from "../../../../context/FileProvider";
import { ClipLoader } from "react-spinners";
import base64ToFile from "../../../../utils/base64ToFile";
import { socketInstance } from "../../../../socket/socket";
import SelectedReplyMessage from "../ReplyMessage/SelectedReplyMessage";
import SelectedEditMessage from "../SelectedMessageOption/SelectedEditMessage";
import {
  appendMessageWithDateCheck,
  updateMessagesByIds,
} from "../../../../store/Slices/MessageListSlice";
import { updateByChatId } from "../../../../store/Slices/ChatListSlice";
import { store } from "../../../../store/store";
import { useScrollToBottom } from "react-scroll-to-bottom";
import { useEffect, useRef, useState, useCallback } from "react";
import { updateViewState } from "../../../../store/Slices/ViewManagerSlice";
import toast from "react-hot-toast";
import { updateCurrentConversation } from "../../../../store/Slices/CurrentConversationSlice";
import { useTranslateText } from "../../../../hooks/useTranslateText";
import { isLink } from "../../../../utils/isLink";
import { useConversationMedia } from "../../../../store/api/useConversationMedia";
import { useConversationDocuments } from "../../../../store/api/useConversationDocuments";
import { useConversationLinks } from "../../../../store/api/useConversationLinks";
import type { ChatDraftAttachment } from "../../../../context/chatAttachmentTypes";
import { inferChatDraftKind } from "../../../../context/chatAttachmentTypes";
import { useLocation } from "react-router-dom";
import {
  makeMessageTxtFile,
  MESSAGE_TXT_NAME,
  messagesFromSendAck,
  shouldSendTextAsFile,
  splitTextIntoBubbles,
  TEXT_AS_FILE_MIN_CHARS,
} from "../../../../utils/textBubbleSplit";
import SendLongTextAsFileModal from "./SendLongTextAsFileModal";

export default function SendMessage() {
  // @ts-ignore
  const { theme } = useTheme();
  const location = useLocation();
  const isVideoCallRoute = location.pathname === "/video-call";
  const messageData = useAppSelector((state) => state.SendMessageData);
  const currentConversationData = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const { refetch: refetchConversationMedia } = useConversationMedia();
  const { refetch: refetchConversationDocuments } = useConversationDocuments();
  const { refetch: refetchConversationLinks } = useConversationLinks();
  const [isStoppedTyping, setIsStoppedTyping] = useState(true);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [, setIsKeyboardOpen] = useState(false);

  const { chatAttachments, removeChatAttachment, addChatAttachment } =
    useFile();

  const dispatch = useAppDispatch();
  const { loading: sendMessageLoading, progress, postData } = useApiPost();
  const scrollToBottom = useScrollToBottom();
  const translate = useTranslateText();

  const [uploadingAttachmentId, setUploadingAttachmentId] = useState<
    string | null
  >(null);
  const [completingIds, setCompletingIds] = useState<Set<string>>(new Set());
  const [composeBusy, setComposeBusy] = useState(false);
  /** If set to current URL — user dismissed OG; send as plain text for web+mobile */
  const [linkPreviewDismissedUrl, setLinkPreviewDismissedUrl] = useState<
    string | null
  >(null);
  /** Optimistic Webpage from compose draft (same shape as mobile multipart) */
  const [draftLinkMeta, setDraftLinkMeta] = useState<{
    title: string;
    description: string;
    image: string;
    favicon: string;
    publisher: string;
  } | null>(null);
  /** TG: ≥8×4096 → offer Message.txt */
  const [longTextFileModal, setLongTextFileModal] = useState<{
    text: string;
  } | null>(null);
  const [longTextFileSending, setLongTextFileSending] = useState(false);

  const keydownListenerRef = useRef<(() => void) | undefined>(undefined);
  const sendMessageApiCallRef = useRef<(e: any) => void>(() => {});
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const hasGifPreview =
    messageData.message_type === "gif" && !!messageData.message?.trim();

  useEffect(() => {
    const isLargeScreen = window.innerWidth >= 1024;
    if (isLargeScreen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [
    currentConversationData.chat_id,
    messageData.reply_to,
    messageData.edit_message_id,
  ]);

  // Reset link-preview dismiss / edit draft when switching chats
  useEffect(() => {
    setLinkPreviewDismissedUrl(null);
    setDraftLinkMeta(null);
    if ((messageData.edit_message_id ?? 0) > 0) {
      dispatch(
        updateSendMessageData({
          edit_message_id: 0,
          edit_message_original: "",
          message: "",
          message_type: "",
        }),
      );
    }
  }, [currentConversationData.chat_id]);

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        keydownListenerRef.current?.();
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  useEffect(() => {
    keydownListenerRef.current = () => {
      if (composeBusy) return;
      const md = messageData;
      const textOk = md.message?.trim() !== "";
      const g = md.message_type === "gif" && !!md.message?.trim();
      if (textOk || chatAttachments.length > 0 || g) {
        void sendMessageApiCallRef.current();
      }
    };
  }, [messageData, chatAttachments.length, composeBusy]);

  const applySendSuccess = useCallback(
    (sendMessageRes: Message) => {
      const parts = messagesFromSendAck(sendMessageRes) as Message[];
      if (!parts.length) return false;

      const head = parts[0];
      if (
        parts.some(
          (m) => m.message_type == "image" || m.message_type == "video",
        )
      ) {
        refetchConversationMedia();
      }
      if (parts.some((m) => m.message_type == "doc")) {
        refetchConversationDocuments();
      }
      if (parts.some((m) => m.message_type == "link")) {
        refetchConversationLinks();
      }
      socketInstance().emit("typing", {
        chat_id: currentConversationData.chat_id,
        typing: false,
      });
      setIsStoppedTyping(true);
      // @ts-ignore
      if (head.is_block) {
        toast.error(`${currentConversationData.user_name} Blocked You! `);
        return false;
      }

      const openChatId = currentConversationData.chat_id;
      const viewingPast = store.getState().MessageOptions.has_more_newer;
      const me = store.getState().userData;

      for (const part of parts) {
        const { messages: _splitIgnore, ...plain } = part as Message & {
          messages?: Message[];
        };
        const sameOpenChat =
          openChatId == 0 ||
          openChatId == -1 ||
          String(openChatId) === String(plain.chat_id);
        if (!sameOpenChat || viewingPast || !plain?.message_id) continue;

        const already = store
          .getState()
          .MessageList.message_list.Records.some(
            (m) => m.message_id === plain.message_id,
          );
        if (already) continue;

        const withUser: Message =
          plain.User || !me?.user_id
            ? plain
            : {
                ...plain,
                User: {
                  user_id: me.user_id,
                  user_name: me.user_name || "",
                  full_name: me.full_name || me.user_name || "",
                  profile_pic: me.profile_pic || "",
                  email: me.email || "",
                  first_name: me.first_name || "",
                  last_name: me.last_name || "",
                  gender: me.gender || "",
                } as Message["User"],
              };
        dispatch(appendMessageWithDateCheck(withUser));
      }

      scrollToBottom();
      if (
        currentConversationData.chat_id == 0 ||
        currentConversationData.chat_id == -1
      ) {
        dispatch(
          updateCurrentConversation({
            chat_id: head.chat_id,
          }),
        );
      }
      return true;
    },
    [
      currentConversationData.chat_id,
      currentConversationData.user_name,
      dispatch,
      refetchConversationDocuments,
      refetchConversationLinks,
      refetchConversationMedia,
      scrollToBottom,
    ],
  );

  const appendCommonFormFields = (messageFormData: FormData) => {
    if (
      currentConversationData.chat_id != -1 &&
      currentConversationData.chat_id != 0
    ) {
      messageFormData.append(
        "chat_id",
        currentConversationData.chat_id.toString(),
      );
    } else {
      messageFormData.append(
        "user_id",
        String(currentConversationData.user_id),
      );
    }
    if (messageData.forward_id) {
      messageFormData.append("forward_id", messageData.forward_id.toString());
    }
    if (messageData.reply_to) {
      messageFormData.append("reply_to", messageData.reply_to.toString());
    }
  };

  const waitCompleteAnimation = (id: string) =>
    new Promise<void>((resolve) => {
      setCompletingIds((prev) => new Set(prev).add(id));
      window.setTimeout(() => {
        removeChatAttachment(id);
        setCompletingIds((prev) => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
        resolve();
      }, 480);
    });

  const postDraftAttachment = async (
    draft: ChatDraftAttachment,
    caption = "",
  ) => {
    const messageFormData = new FormData();
    appendCommonFormFields(messageFormData);

    switch (draft.kind) {
      case "image": {
        messageFormData.append("message_type", "image");
        messageFormData.append("files", draft.file);
        messageFormData.append("message_content", "");
        if (caption) messageFormData.append("caption", caption);
        break;
      }
      case "doc": {
        messageFormData.append("message_type", "doc");
        messageFormData.append("files", draft.file);
        messageFormData.append("message_content", "");
        messageFormData.append("message_length", draft.file.name || "");
        if (draft.file.size > 0) {
          messageFormData.append("message_size", String(draft.file.size));
        }
        break;
      }
      case "video": {
        if (!draft.videoThumbnail) {
          toast.error(translate("Please wait for video preview"));
          return null;
        }
        const thumbnailBlob = base64ToFile(
          draft.videoThumbnail,
          "image/png",
          "thumbnail.png",
        );
        messageFormData.append("message_type", "video");
        messageFormData.append("files", thumbnailBlob);
        messageFormData.append("files", draft.file);
        messageFormData.append("message_content", "");
        if (caption) messageFormData.append("caption", caption);
        break;
      }
      default:
        return null;
    }

    if (messageData.status_id) {
      messageFormData.append("story_id", messageData.status_id.toString());
      messageFormData.delete("message_type");
      messageFormData.append("message_type", "story_reply");
    }

    const { data: sendMessageRes }: { data: Message } = await postData(
      "chat/send-message",
      messageFormData,
      "multipart/form-data",
    );
    return sendMessageRes;
  };

  async function sendMessageApiCall(e?: any) {
    e?.preventDefault?.();
    if (composeBusy) return;

    if (
      !currentConversationData.chat_id &&
      currentConversationData.user_id == 0
    ) {
      return;
    }

    const textRaw = messageData.message ?? "";
    const hasText = textRaw.trim() !== "";
    const hasGif = messageData.message_type === "gif" && !!messageData.message;
    const draftsSnapshot = [...chatAttachments];
    const editId = Number(messageData.edit_message_id) || 0;

    // Edit existing text/link message
    if (editId > 0) {
      if (!hasText) return;
      setComposeBusy(true);
      try {
        const res = await postData("chat/edit-message", {
          message_id: editId,
          message_content: textRaw.trim(),
        });
        const edited = (res?.data ?? res) as Message | undefined;
        if (edited?.message_id) {
          dispatch(
            updateMessagesByIds({
              message_id: edited.message_id,
              message: edited.message_content,
              message_type: edited.message_type,
              is_edited: true,
            }),
          );
          const chatPreview = store
            .getState()
            .chatList.Chats.find(
              (c) => c.Records[0]?.chat_id == edited.chat_id,
            );
          const lastId = chatPreview?.Records[0]?.Messages?.[0]?.message_id;
          if (lastId == edited.message_id) {
            dispatch(
              updateByChatId({
                chat_id: edited.chat_id,
                last_message: edited.message_content,
                last_message_type: edited.message_type,
              }),
            );
          }
          dispatch(
            updateSendMessageData({
              edit_message_id: 0,
              edit_message_original: "",
              message: "",
              message_type: "",
              reply_to: 0,
            }),
          );
          if (inputRef.current) inputRef.current.style.height = "auto";
        } else {
          toast.error(res?.message || "Failed to edit message");
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Failed to edit message");
      } finally {
        setComposeBusy(false);
      }
      return;
    }

    if (!hasText && !hasGif && draftsSnapshot.length === 0) return;

    if (messageData.message_type === "video" && draftsSnapshot.length > 0) {
      const v = draftsSnapshot.find((d) => d.kind === "video");
      if (v && !v.videoThumbnail) {
        toast.error(translate("Please wait for video preview"));
        return;
      }
    }

    // ≥8×4096 → Message.txt modal (before busy spinner)
    if (hasText && shouldSendTextAsFile(textRaw)) {
      setLongTextFileModal({ text: textRaw });
      return;
    }

    setComposeBusy(true);

    try {
      /* ── GIF only ── */
      if (hasGif && !hasText && draftsSnapshot.length === 0) {
        const messageFormData = new FormData();
        appendCommonFormFields(messageFormData);
        messageFormData.append("message_type", "gif");
        messageFormData.append("message_content", messageData.message!);
        if (messageData.status_id) {
          messageFormData.append("story_id", messageData.status_id.toString());
          messageFormData.delete("message_type");
          messageFormData.append("message_type", "story_reply");
        }
        const { data: sendMessageRes }: { data: Message } = await postData(
          "chat/send-message",
          messageFormData,
          "multipart/form-data",
        );
        if (sendMessageRes && applySendSuccess(sendMessageRes)) {
          dispatch(
            updateSendMessageData({
              message: "",
              message_type: "",
              reply_to: 0,
            }),
          );
          if (inputRef.current) inputRef.current.style.height = "auto";
        }
        return;
      }

      const mediaDrafts = draftsSnapshot.filter(
        (d) => d.kind === "image" || d.kind === "gif" || d.kind === "video",
      );
      const onlyMediaDrafts =
        mediaDrafts.length > 0 &&
        mediaDrafts.length === draftsSnapshot.length;
      // Text + only media → caption on media/album (TG), not a separate bubble
      const captionForMedia =
        hasText && onlyMediaDrafts && !isLink(textRaw) && !hasGif
          ? textRaw.trim()
          : "";
      const sendAsAlbum = mediaDrafts.length >= 2 && onlyMediaDrafts;

      /* ── Text (incl. story reply text) — TG split ≤4096 per bubble ── */
      if (hasText && !captionForMedia) {
        const textChunks = splitTextIntoBubbles(textRaw);
        const wantLinkPreview =
          isLink(textRaw) && linkPreviewDismissedUrl !== textRaw;
        let textOk = true;

        for (let i = 0; i < textChunks.length; i++) {
          const chunk = textChunks[i];
          const messageFormData = new FormData();
          appendCommonFormFields(messageFormData);

          // reply / OG / story only on the first bubble
          if (i === 0) {
            let mtype: string = wantLinkPreview ? "link" : "text";
            if (messageData.status_id) {
              mtype = "story_reply";
              messageFormData.append(
                "story_id",
                messageData.status_id.toString(),
              );
            }
            messageFormData.append("message_type", mtype);
            messageFormData.append("message_content", chunk);
            if (isLink(textRaw)) {
              messageFormData.append(
                "link_preview",
                wantLinkPreview ? "1" : "0",
              );
              if (wantLinkPreview) {
                messageFormData.append("link_url", textRaw.trim());
                if (draftLinkMeta?.title) {
                  messageFormData.append("link_title", draftLinkMeta.title);
                }
                if (draftLinkMeta?.description) {
                  messageFormData.append(
                    "link_description",
                    draftLinkMeta.description,
                  );
                }
                if (draftLinkMeta?.image) {
                  messageFormData.append("link_image", draftLinkMeta.image);
                }
                if (draftLinkMeta?.favicon) {
                  messageFormData.append(
                    "link_favicon",
                    draftLinkMeta.favicon,
                  );
                }
                if (draftLinkMeta?.publisher) {
                  messageFormData.append(
                    "link_site_name",
                    draftLinkMeta.publisher,
                  );
                }
              }
            }
          } else {
            messageFormData.append("message_type", "text");
            messageFormData.append("message_content", chunk);
            messageFormData.append("link_preview", "0");
            // Do not re-attach reply_to on follow-up parts
            messageFormData.delete("reply_to");
          }

          const { data: sendMessageRes }: { data: Message } = await postData(
            "chat/send-message",
            messageFormData,
            "multipart/form-data",
          );
          if (!sendMessageRes || !applySendSuccess(sendMessageRes)) {
            textOk = false;
            break;
          }
        }

        if (!textOk) {
          toast.error("Failed to send message");
          return;
        }
        dispatch(
          updateSendMessageData({
            message: "",
            message_type: "",
          }),
        );
        setLinkPreviewDismissedUrl(null);
        setDraftLinkMeta(null);
        if (inputRef.current) inputRef.current.style.height = "auto";
      }

      /* ── GIF after text (if both) ── */
      if (hasGif && (hasText || draftsSnapshot.length > 0)) {
        const messageFormData = new FormData();
        appendCommonFormFields(messageFormData);
        messageFormData.append("message_type", "gif");
        messageFormData.append("message_content", messageData.message!);
        const { data: gifRes }: { data: Message } = await postData(
          "chat/send-message",
          messageFormData,
          "multipart/form-data",
        );
        if (gifRes && applySendSuccess(gifRes)) {
          dispatch(
            updateSendMessageData({
              message: "",
              message_type: "",
            }),
          );
        }
      }

      /* ── Media album (N≥2) ── */
      if (sendAsAlbum) {
        for (const d of mediaDrafts) {
          if (d.kind === "video" && !d.videoThumbnail) {
            toast.error(translate("Please wait for video preview"));
            return;
          }
        }
        const form = new FormData();
        appendCommonFormFields(form);
        for (const d of mediaDrafts) {
          if (d.kind === "video" && d.videoThumbnail) {
            const thumbnailBlob = base64ToFile(
              d.videoThumbnail,
              "image/png",
              "thumbnail.png",
            );
            // Group endpoint detects by mimetype of each file; send video only
            form.append("files", d.file);
            void thumbnailBlob;
          } else {
            form.append("files", d.file);
          }
        }
        if (captionForMedia) form.append("caption", captionForMedia);
        setUploadingAttachmentId(mediaDrafts[0]?.id || null);
        try {
          const { data: groupRes } = await postData(
            "chat/send-media-group",
            form,
            "multipart/form-data",
          );
          const msgs = (groupRes?.messages || []) as Message[];
          if (msgs.length) {
            for (const m of msgs) applySendSuccess(m);
            for (const d of mediaDrafts) await waitCompleteAnimation(d.id);
            if (captionForMedia) {
              dispatch(
                updateSendMessageData({
                  message: "",
                  message_type: "",
                }),
              );
              if (inputRef.current) inputRef.current.style.height = "auto";
            }
          } else {
            toast.error(groupRes?.message || "Failed to send album");
            return;
          }
        } finally {
          setUploadingAttachmentId(null);
        }
      } else {
        /* ── Attachments queue (singles) ── */
        let captionUsed = false;
        for (const draft of draftsSnapshot) {
          if (draft.kind === "video" && !draft.videoThumbnail) {
            toast.error(translate("Please wait for video preview"));
            break;
          }
          setUploadingAttachmentId(draft.id);
          try {
            const cap =
              !captionUsed && captionForMedia ? captionForMedia : "";
            if (cap) captionUsed = true;
            const sendMessageRes = await postDraftAttachment(draft, cap);
            if (sendMessageRes && applySendSuccess(sendMessageRes)) {
              await waitCompleteAnimation(draft.id);
              if (cap) {
                dispatch(
                  updateSendMessageData({
                    message: "",
                    message_type: "",
                  }),
                );
                if (inputRef.current) inputRef.current.style.height = "auto";
              }
            } else {
              break;
            }
          } finally {
            setUploadingAttachmentId(null);
          }
        }
      }

      dispatch(
        updateSendMessageData({
          reply_to: 0,
          edit_message_id: 0,
          edit_message_original: "",
          thumbnail_url: "",
          fileName: "",
        }),
      );
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to send message",
      );
    } finally {
      setComposeBusy(false);
      setUploadingAttachmentId(null);
    }
  }

  sendMessageApiCallRef.current = sendMessageApiCall;

  async function sendTypingStatus() {
    if (isStoppedTyping) {
      socketInstance().emit("typing", {
        chat_id: currentConversationData.chat_id,
        typing: true,
      });
      setIsStoppedTyping(false);
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      socketInstance().emit("typing", {
        chat_id: currentConversationData.chat_id,
        typing: false,
      });
      setIsStoppedTyping(true);
    }, 3000);
  }

  const sendLongTextAsMessageFile = useCallback(
    async (text: string, fileName: string = MESSAGE_TXT_NAME) => {
      if (!text) return false;
      setLongTextFileSending(true);
      setComposeBusy(true);
      try {
        const file = makeMessageTxtFile(text, fileName);
        const messageFormData = new FormData();
        appendCommonFormFields(messageFormData);
        messageFormData.append("message_type", "doc");
        messageFormData.append("files", file);
        messageFormData.append("message_content", "");
        messageFormData.append("message_length", file.name || MESSAGE_TXT_NAME);
        messageFormData.append("message_size", String(file.size));
        const { data: sendMessageRes }: { data: Message } = await postData(
          "chat/send-message",
          messageFormData,
          "multipart/form-data",
        );
        if (!sendMessageRes || !applySendSuccess(sendMessageRes)) {
          toast.error(translate("Failed to send message"));
          return false;
        }
        dispatch(
          updateSendMessageData({
            message: "",
            message_type: "",
          }),
        );
        setLinkPreviewDismissedUrl(null);
        if (inputRef.current) inputRef.current.style.height = "auto";
        return true;
      } catch {
        toast.error(translate("Failed to send message"));
        return false;
      } finally {
        setLongTextFileSending(false);
        setLongTextFileModal(null);
        setComposeBusy(false);
      }
    },
    // appendCommonFormFields closes over latest messageData / conversation
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      applySendSuccess,
      dispatch,
      postData,
      messageData,
      currentConversationData,
      translate,
    ],
  );

  const onPasteCompose = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length > 0) {
      let used = false;
      for (const f of files) {
        const k = inferChatDraftKind(f);
        if (k) {
          e.preventDefault();
          const sizeMb = f.size / (1024 * 1024);
          if (sizeMb > 100) {
            toast.error("File size is greater than 100 MB", { duration: 3000 });
            continue;
          }
          addChatAttachment(f, k);
          used = true;
        }
      }
      if (used) {
        dispatch(
          updateSendMessageData({
            showAttachmentOptions: false,
            message_type: "",
          }),
        );
      }
      return;
    }

    const pasted = e.clipboardData?.getData("text") || "";
    if (!pasted) return;
    const el = e.currentTarget;
    const start = el.selectionStart ?? (messageData.message || "").length;
    const end = el.selectionEnd ?? start;
    const cur = messageData.message || "";
    const next = cur.slice(0, start) + pasted + cur.slice(end);
    if (
      pasted.length >= TEXT_AS_FILE_MIN_CHARS ||
      next.length >= TEXT_AS_FILE_MIN_CHARS
    ) {
      e.preventDefault();
      // Prefer full resulting text; if only paste is huge, send that blob
      const payload =
        next.length >= TEXT_AS_FILE_MIN_CHARS && next.length >= pasted.length
          ? next
          : pasted;
      setLongTextFileModal({ text: payload });
    }
  };

  // Only local composeBusy — sendMessageLoading can stick true if a request hangs
  // and was blocking the airplane button forever.
  const busy = composeBusy;
  const firstVideo = chatAttachments.find((d) => d.kind === "video");
  const videoNotReady =
    firstVideo && !firstVideo.videoThumbnail && chatAttachments.length > 0;

  const composeText = (messageData.message || "").trim();
  const showLinkDraft =
    isLink(composeText) &&
    linkPreviewDismissedUrl !== composeText &&
    !hasGifPreview &&
    chatAttachments.length === 0 &&
    !["location", "gif"].includes(messageData.message_type || "");

  return (
    <div
      className={
        isVideoCallRoute
          ? "relative flex w-full items-center justify-center bg-primary pb-3 pt-2"
          : "absolute inset-x-0 bottom-0 z-30 flex w-full items-center justify-center bg-primary/95 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur-sm transition-all duration-300 sm:bg-primary/90 md:bg-transparent md:backdrop-blur-none"
      }
    >
      <div className="relative z-30 flex w-[90%] items-end gap-2 xl:w-[90%]">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <AttachmentDraftStrip
            attachments={chatAttachments}
            gifUrl={hasGifPreview ? messageData.message! : null}
            onRemoveAttachment={removeChatAttachment}
            onClearGif={() => {
              dispatch(
                updateSendMessageData({
                  message: "",
                  message_type: "",
                }),
              );
            }}
            uploadingId={uploadingAttachmentId}
            uploadProgress={progress}
            completingIds={completingIds}
          />

          {showLinkDraft ? (
            <LinkDraftPreview
              url={composeText}
              onDismiss={() => {
                setLinkPreviewDismissedUrl(composeText);
                setDraftLinkMeta(null);
              }}
              onMeta={(m) => setDraftLinkMeta(m)}
            />
          ) : null}

          <div
            className={`relative flex max-h-40 w-full min-w-0 flex-col gap-0 rounded-xl border bg-secondary ${theme == "dark" ? "border-[#EEEEEE14]" : "border-[#B0B0B0]"} `}
          >
            <div className="flex w-full min-w-0 items-end gap-1 overflow-x-clip">
              <EmojiPickerCompo />
              <SelectedReplyMessage />
              <SelectedEditMessage />
              <form className="min-w-0 flex-1">
                <textarea
                  ref={inputRef}
                  value={
                    ["location", "gif"].includes(messageData.message_type!)
                      ? ""
                      : messageData.message
                  }
                  onChange={(e) => {
                    const textarea = e.target;
                    textarea.style.height = "auto";
                    textarea.style.height = `${textarea.scrollHeight}px`;
                    sendTypingStatus();
                    const value = textarea.value;
                    if (value.trim() === "") {
                      textarea.style.height = "auto";
                    }
                    dispatch(
                      updateSendMessageData({
                        chat_id: currentConversationData.chat_id,
                        message: value,
                        message_type: "text",
                      }),
                    );
                  }}
                  onPaste={onPasteCompose}
                  onFocus={() => {
                    setIsKeyboardOpen(true);
                    dispatch(
                      updateViewState({
                        showOtherProfile: false,
                        showSearchMessage: false,
                        showStarMessageList: false,
                        showMediaDocLinks: false,
                      }),
                    );
                  }}
                  onBlur={() => setIsKeyboardOpen(false)}
                  className="h-full w-full resize-none bg-transparent px-2 py-2 align-middle text-sm leading-8 placeholder-lightText outline-none"
                  placeholder={translate("Type Message")}
                  rows={1}
                  style={{
                    maxHeight: "9rem",
                    overflowY: "auto",
                  }}
                />
              </form>
              <SelectFile />
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            if (videoNotReady) {
              toast.error(translate("Please wait for video preview"));
              return;
            }
            void sendMessageApiCall();
          }}
          disabled={busy && !videoNotReady}
          className={`relative z-50 primary-gradient grid h-12 w-12 shrink-0 place-content-center rounded-xl ${
            videoNotReady || busy
              ? "cursor-not-allowed opacity-60"
              : "cursor-pointer"
          }`}
        >
          {busy ? (
            <ClipLoader size={25} color="#ffffff" />
          ) : (
            <SendIcon className="-rotate-0 text-[26px]" />
          )}
        </button>
      </div>

      <SendLongTextAsFileModal
        open={!!longTextFileModal}
        text={longTextFileModal?.text || ""}
        sending={longTextFileSending}
        onCancel={() => {
          if (!longTextFileSending) setLongTextFileModal(null);
        }}
        onConfirm={(fileName) => {
          if (longTextFileModal?.text) {
            void sendLongTextAsMessageFile(longTextFileModal.text, fileName);
          }
        }}
      />
    </div>
  );
}
